import type { PublicVenueCatalogState } from './domain'

export type CityOption = {
  id: string
  label: string
  city: string
  region?: string
  country?: string
  latitude?: number
  longitude?: number
  source: 'tables' | 'openstreetmap'
}

export function availableCityLabels(catalog: PublicVenueCatalogState) {
  if (catalog.status !== 'ready') return usableLabel(catalog.market) ? [catalog.market] : []

  const labels = catalog.venues.flatMap((venue) => {
    const addressLabel = venue.address?.city
      ? [venue.address.city, venue.address.region].filter(Boolean).join(', ')
      : undefined
    return [addressLabel, venue.market]
  })

  return uniqueLabels([catalog.market, ...labels])
}

export function cityOptionFromLabel(label: string): CityOption {
  const [city, ...regionParts] = label.split(',').map((part) => part.trim()).filter(Boolean)
  return {
    id: `tables:${normalize(label)}`,
    label,
    city: city || label,
    region: regionParts.join(', ') || undefined,
    source: 'tables',
  }
}

export function mergeCityOptions(preferred: CityOption[], remote: CityOption[]) {
  const seen = new Set<string>()
  return [...preferred, ...remote].filter((option) => {
    const key = normalize(option.label)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function uniqueLabels(labels: Array<string | undefined>) {
  const seen = new Set<string>()
  return labels.filter((label): label is string => {
    if (!usableLabel(label)) return false
    const key = normalize(label)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function usableLabel(label: string | undefined): label is string {
  return Boolean(label?.trim() && label.trim().toLowerCase() !== 'your area')
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
